// src/lib/caution-restitution-preview.js — MÉCANIQUE #21 « caution restituée »
// (chantier B, ADR-272) : moteur PUR de l'aperçu EN DIRECT côté admin.
//
// EXIGENCE FORTE Dimitri : pendant qu'il tape le « Motif du prélèvement » dans la
// modale caution, il DOIT voir le message #21 FINAL (tel que le voyageur le
// recevra), mis à jour EN TEMPS RÉEL. Ce module fournit la dérivation PURE
// (flags + tokens) et le rendu 2 passes RÉUTILISANT le moteur Direct — 0 réseau,
// 0 I/O → un simple useMemo suffit (mise à jour synchrone à chaque frappe).
//
// PARITÉ SERVEUR (byte-identique à renderDirectRich) : le rendu rejoue EXACTEMENT
// les 2 passes du serveur —
//   1. passe conditionnelle : evaluateConditions(body, {vars}, resolver) — même
//      parseur {#if} que lib/template-variables.js (mirroir client src/lib) ;
//   2. passe interpolation : {token} → valeur.
// La dérivation des flags MIROITE lib/direct-vars-catalog.js
// (deriveCautionRestitutionFlags + caution_empreinte/débit via cautionModeForNights).
// Un test de parité (caution-restitution-preview.test.js) verrouille l'identité
// client↔serveur pour les 6 combinaisons de conditions.
//
// ⚠️ CHEMIN ARGENT — L'APERÇU EST COSMÉTIQUE : les montants affichés ici NE
// DÉCIDENT RIEN de ce qui part à Stripe. montant_libere = caution − prélevé est
// dérivé pour l'affichage uniquement ; la vérité argent reste la DB/Stripe
// (montant réellement capturé / libéré), posée côté serveur.

import { evaluateConditions } from "./template-variables";
import { cautionModeForNights } from "./caution-mode";

// Résolveur de contexte Direct côté client — MIROIR de directConditionResolver
// (lib/direct-rich-render.js) : lit ctx.vars[name], non-vide (trim) = vrai,
// absent → null (bloc {#if} masqué, fail-safe).
function directConditionResolver(name, ctx) {
  const vars = (ctx && ctx.vars) || {};
  if (Object.prototype.hasOwnProperty.call(vars, name)) {
    const v = vars[name];
    return v == null ? "" : String(v);
  }
  return null;
}

// Trichotomie P vs C — MIROIR EXACT de deriveCautionRestitutionFlags
// (lib/direct-vars-catalog.js). C/P non-finis ⇒ aucune posée (fail-doux).
function _isNonEmpty(v) {
  return v != null && String(v).trim() !== "";
}

export function deriveCautionRestitutionFlags(cautionAmount, montantPreleve) {
  // null/undefined/"" → 0 via Number() : on les REJETTE (fail-doux, miroir serveur).
  if (!_isNonEmpty(cautionAmount) || !_isNonEmpty(montantPreleve)) return {};
  const C = Number(cautionAmount);
  const P = Number(montantPreleve);
  if (!Number.isFinite(C) || !Number.isFinite(P)) return {};
  const TRUE = "1";
  return {
    liberation_totale: P <= 0 ? TRUE : "",
    prelevement_partiel: P > 0 && P < C ? TRUE : "",
    prelevement_total: P >= C && P > 0 ? TRUE : "",
  };
}

/**
 * Construit le contexte `vars` (flags + tokens) de l'aperçu #21 à partir des
 * données réelles de la caution + du motif LIVE saisi. PUR.
 *
 * @param {object} args
 *   cautionAmount  {number}  — montant de la caution (C).
 *   montantPreleve {number}  — montant prélevé (P) : 0 pour une libération/release.
 *   nbNuits        {number}  — nombre de nuits (axe empreinte/débit, ADR-174).
 *   motif          {string}  — « Motif du prélèvement » (texte libre admin, LIVE).
 *   guestFirstName {string}
 *   logementName   {string}
 * @returns {object} vars (flags "1"/"" + tokens) pour le rendu 2 passes.
 */
export function buildCautionPreviewVars({
  cautionAmount,
  montantPreleve,
  nbNuits,
  motif,
  guestFirstName,
  logementName,
} = {}) {
  const C = Number(cautionAmount);
  const P = Number(montantPreleve);
  const hasC = Number.isFinite(C);
  const hasP = Number.isFinite(P);

  // Axe MODE (nb nuits) — MIROIR deriveDirectConditionFlags.
  const mode = cautionModeForNights(Number(nbNuits));

  // Axe ISSUE (P vs C).
  const restitFlags = deriveCautionRestitutionFlags(C, P);

  const libere = hasC && hasP ? Math.max(0, C - P) : "";

  return {
    // Tokens
    caution_montant: hasC ? C : "",
    montant_preleve: hasP ? P : "",
    montant_libere: libere,
    motif_prelevement: (motif || "").trim(),
    guest_first_name: guestFirstName || "",
    logement_name: logementName || "",
    // Flags empreinte/débit ("1"/"")
    caution_empreinte: mode === "empreinte" ? "1" : "",
    caution_debit: mode === "debit" ? "1" : "",
    // Flags trichotomie restitution
    ...restitFlags,
  };
}

// Interpolation {token} — MIROIR de interpolatePreview (DirectMessagesView) :
// remplace {token} par sa valeur, laisse le littéral si absent.
function interpolate(text, vars) {
  if (typeof text !== "string") return "";
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, tok) => {
    const v = vars[tok];
    return v == null ? whole : String(v);
  });
}

/**
 * Rend un corps de gabarit #21 en 2 passes (conditionnelle PUIS interpolation) —
 * PARITÉ byte-identique avec renderDirectRich côté serveur.
 * @returns {string} corps rendu, prêt à afficher.
 */
export function renderCautionPreview(body, vars) {
  if (typeof body !== "string") return "";
  const conditioned = evaluateConditions(body, { vars: vars || {} }, directConditionResolver);
  return interpolate(conditioned, vars);
}

// ── Corps de DÉMO (#21) — APERÇU UNIQUEMENT, JAMAIS ENVOYÉ ────────────────────
//
// ⚠️ La POSE du texte #21 = chantier B7 (via l'éditeur riche), avec les 22 autres
// messages. Tant que le slug `caution_restituee` n'est pas posé dans le store
// Direct, l'aperçu se nourrit de ce VERBATIM DE RÉFÉRENCE (validé Dimitri, gravé
// dans docs/handoff/messages-direct-v2-a-poser.md § « 21 — Caution restituée »).
// Ce n'est PAS le gabarit d'envoi : aucun sender ne lit cette constante.
export const CAUTION_RESTITUEE_DEMO_SUBJECT =
  "Votre dépôt de garantie vous a été restitué — {logement_name}";

export const CAUTION_RESTITUEE_DEMO_BODY = [
  "Bonjour {guest_first_name},",
  "",
  "Nous vous confirmons que votre dépôt de garantie de {caution_montant} € vous a été restitué.",
  "",
  "{#if caution_empreinte}L'empreinte bancaire posée sur votre carte a été libérée. Aucun montant n'a été prélevé.{/if}",
  "{#if caution_debit}Le remboursement a été effectué sur la carte utilisée lors de votre réservation. Il apparaîtra sur votre compte sous 5 à 10 jours ouvrés, selon votre banque.{/if}",
  "",
  "Merci encore pour votre séjour à {logement_name}.",
  "",
  "Au plaisir de vous accueillir à nouveau,",
  "L'équipe FULLBNBZEN",
].join("\n");
